<script lang="ts" setup>
import { Overlay, showLoadingToast, showNotify } from "vant";
import VueCropper, { VueCropperMethods } from "vue-cropperjs";
import { computed, ref, watch } from "vue";
import { base64ToFile } from "@/modules/common";
import GIF from "gif.js";
import { GifToCanvas } from "@/modules/gifToCanvas";
import i18n from "@/locales";
import { useDebounceFn, useObjectUrl } from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    showRatioSelect?: boolean;
    cropAspectRatio?: number;
    isCropGif?: boolean;
    onFinished?: (
      base64: string,
      file: File,
      otherFiles: File[]
    ) => Promise<any> | any;
    allowCropOutside?: boolean;
  }>(),
  {
    cropAspectRatio: 3 / 4,
    onFinished: () => {},
    isCropGif: false,
    allowCropOutside: true,
  }
);

const emit = defineEmits(["update:cropAspectRatio", "onCancel"]);

const show = ref(false);
const cropper = ref<VueCropperMethods | null>(null);
const cropperOthers = ref<VueCropperMethods[] | null>(null);
const originUrl = ref("");
const blobRef = ref<Blob | undefined>();
const originalImage = useObjectUrl(blobRef);
const isCropperUploading = ref(false);
const cropperReady = ref(false);
const imageLoadFailed = ref(false);
const imageLoadFailMsg = ref("");
const imageLoading = ref(false);

const baseWidth = computed(() =>
  props.cropAspectRatio === 3 / 4 ? 768 : 1280
);
const baseHeight = computed(() => baseWidth.value / props.cropAspectRatio);
const debouncedResetCropper = useDebounceFn(
  () => {
    loadImageAndGetObjectUrl(originUrl.value);
  },
  300,
  { maxWait: 5000 }
);
// Lifecycle
watch(show, () => {
  if (show.value) {
    addEventListener("resize", debouncedResetCropper);
  }
  if (!show.value) {
    removeEventListener("resize", debouncedResetCropper);
    clear();
  }
});

watch(
  () => props.cropAspectRatio,
  () => {
    cropper.value?.setAspectRatio(props.cropAspectRatio);
  }
);

const others = ref<string[]>([]);

const loadImageAndGetObjectUrl = async (urlOrBase64: string) => {
  imageLoading.value = true;
  imageLoadFailed.value = false;
  try {
    const res = await fetch(urlOrBase64, {
      credentials: "omit",
      mode: "cors",
    });
    blobRef.value = await res.blob();
    cropper.value?.replace(originalImage.value!);
    imageLoading.value = false;
  } catch (e) {
    imageLoadFailed.value = true;
    imageLoadFailMsg.value = (e as Error).message;
    showNotify({
      type: "danger",
      message: (e as Error).message,
    });
  } finally {
    imageLoading.value = false;
  }
};

// Event Handler
function showCropper(content: string, _others?: string[]) {
  originUrl.value = content;
  loadImageAndGetObjectUrl(content);
  show.value = true;
  others.value = _others || [];
}

function clear() {
  show.value = false;
  cropperReady.value = false;
  cropper.value?.reset();
  blobRef.value = undefined;
  originUrl.value = "";
  isCropperUploading.value = false;
  imageLoadFailed.value = false;
  imageLoading.value = false;
  imageLoadFailMsg.value = "";
}

const onConfirmCrop = async () => {
  let result = "";
  isCropperUploading.value = true;
  const isCropGif = props.isCropGif && cropper.value?.src.includes(".gif");
  let otherFiles: File[] = [];
  if (isCropGif) {
    const loading = showLoadingToast({
      message: i18n.global.t("Message.inCutting"),
      zIndex: 9999,
    });
    result = await cropGifHandle();
    loading.close();
  } else {
    const canvasData = cropper.value!.getCanvasData();
    const cropBoxData = cropper.value!.getCropBoxData();
    const cropperData = cropper.value!.getData(true);

    if (others.value.length) {
      cropperOthers.value?.forEach((cropper) => {
        cropper.reset();
        cropper.setAspectRatio(props.cropAspectRatio);
        const localCropBox = cropper.getCropBoxData();

        var zoom =
          (canvasData.width / canvasData.naturalWidth) *
          (localCropBox.width / cropBoxData.width);

        cropper.zoomTo(zoom);

        const localData = cropper.getData(true);
        const relativeX = (localData.x - cropperData.x) * zoom;
        const relativeY = (localData.y - cropperData.y) * zoom;
        cropper.move(relativeX, relativeY);

        const file = base64ToFile(
          cropper
            .getCroppedCanvas({
              width: baseWidth.value,
              height: baseHeight.value,
              maxWidth: 1000,
            })
            .toDataURL(),
          `${new Date().toLocaleString().replace(/\D/g, "")}.png`
        );
        otherFiles.push(file);
      });
    }

    result = cropper
      .value!.getCroppedCanvas({
        width: baseWidth.value,
        height: baseHeight.value,
      })
      .toDataURL();
  }

  const croppedImageFile = isCropGif
    ? new File([result], "image.gif", { type: "image/gif" })
    : base64ToFile(
        result,
        `${new Date().toLocaleString().replace(/\D/g, "")}.${
          isCropGif ? "gif" : "png"
        }`
      );
  try {
    if (!props.onFinished) return;
    await props.onFinished(result, croppedImageFile, otherFiles);
    show.value = false;
    clear();
  } finally {
    isCropperUploading.value = false;
  }
};

const dataURLtoBlob = (dataurl: string) => {
  var arr = dataurl.split(","),
    //@ts-ignore
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

let gifToCanvas: any = null;
let gif: any = null;
const cropGifHandle = async (): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    if (cropper.value) {
      const base64 = await chooseStaticImg(cropper.value.src);
      const url = URL.createObjectURL(dataURLtoBlob(base64));
      const cropBoxData = cropper.value.getCropBoxData();
      const canvasData = cropper.value.getCanvasData();
      const ratio = canvasData.naturalWidth / canvasData.width;
      gifToCanvas = new GifToCanvas(url, {
        targetOffset: {
          dx: cropBoxData.left - canvasData.left,
          dy: cropBoxData.top - canvasData.top,
          width: canvasData.naturalWidth,
          height: canvasData.naturalHeight,
          sWidth: cropBoxData.width * ratio,
          sHeight: cropBoxData.height * ratio,
        },
      });
      gif = new GIF({
        workers: 4,
        quality: 10,
        width: cropBoxData.width * ratio,
        height: cropBoxData.height * ratio,
        workerScript: `${window.location.origin}/gif.worker.js`,
      });
      const addFrame = (canvas, delay) => {
        gif.addFrame(canvas, { copy: true, delay });
      };
      gifToCanvas.on("progress", (canvas, delay) => {
        addFrame(canvas, delay);
      });
      gifToCanvas.on("finished", (canvas, delay) => {
        addFrame(canvas, delay);
        gif.render();
      });
      gif.on("finished", (blob: string) => {
        resolve(blob);
      });
      gifToCanvas.init();
    } else {
      reject();
    }
  });
};
const chooseStaticImg = (imageUrl: string) =>
  new Promise((resolve) => {
    getImageFileFromUrl(
      imageUrl,
      i18n.global.t("story.image") + ".gif",
      function (file: File) {
        let reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result);
        };
        reader.readAsDataURL(file);
      }
    );
  });

// 根据路径返回file
const getImageFileFromUrl = (url: string, imageName: string, callback: any) => {
  // 设置不跨域
  fetch(url, {
    credentials: "omit",
    mode: "cors",
  })
    .then((res) => {
      return res.blob();
    })
    .then((blob) => {
      let imgFile = new File([blob], imageName, { type: "image/gif" });
      callback(imgFile);
    });
};

const onCancel = () => {
  clear();
  emit("onCancel");
};

export interface CropImageOverlayInstance {
  showCropper: (content: string, others?: string[]) => void;
}

defineExpose({ showCropper });
</script>
<template>
  <overlay :show="show" class="z-10 w-full h-full">
    <div
      :class="{
        'grid-rows-[auto_1fr_auto]': others?.length,
        'grid-rows-[auto_1fr]': !others?.length,
      }"
      class="w-full h-full bg-base-100 grid overflow-hidden van-safe-area-top"
    >
      <div class="navbar bg-base-100 h-top-nav border-b relative z-10">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="onCancel">
            {{ $t("Buttons.cancel") }}
          </button>
        </div>
        <!-- 中 -->
        <div class="navbar-center">
          <template v-if="showRatioSelect">
            <button
              :class="[
                'btn btn-sm rounded',
                cropAspectRatio === 3 / 4 ? 'btn-primary' : ' opacity-50',
              ]"
              @click="emit('update:cropAspectRatio', '3:4')"
            >
              3:4
            </button>
            <button
              :class="[
                'btn  btn-sm rounded',
                cropAspectRatio === 16 / 9 ? 'btn-primary ' : 'opacity-50',
              ]"
              @click="emit('update:cropAspectRatio', '16:9')"
            >
              16:9
            </button>
          </template>
        </div>
        <!-- 右 -->
        <div class="navbar-end gap-2">
          <button
            :disabled="isCropperUploading || !cropperReady"
            class="btn btn-sm rounded"
            @click="cropper?.reset()"
          >
            {{ $t("Buttons.reset") }}
          </button>
          <button
            :disabled="isCropperUploading || !cropperReady"
            class="btn btn-primary btn-sm rounded"
            @click="onConfirmCrop"
          >
            {{ $t("Buttons.done") }}
          </button>
        </div>
      </div>
      <div
        class="flex items-center justify-center w-full h-full bg-base-200 p-8 overflow-hidden"
      >
        <div
          v-if="imageLoading"
          class="flex flex-col gap-2 justify-center items-center"
        >
          <div class="loading loading-sm" />
          {{ $t("Message.imageLoading") }}
        </div>
        <div
          v-else-if="imageLoadFailed"
          class="flex flex-col justify-center items-center gap-2"
        >
          <XmarkCircleIcon class="icon-lg text-error" />
          <span class="text-error">{{ $t("Message.imageLoadFailed") }}</span>
          <span class="text-error">{{ imageLoadFailMsg }}</span>
          <span class="text-error text-center">{{ originUrl }}</span>
          <button
            class="btn btn-sm btn-neutral"
            @click="loadImageAndGetObjectUrl(originUrl)"
          >
            <i class="iconfont icon-refresh" />
            {{ $t("Message.imageReload") }}
          </button>
        </div>
        <vue-cropper
          v-else
          ref="cropper"
          :aspect-ratio="cropAspectRatio"
          :checkCrossOrigin="false"
          :ready="
            () => {
              cropperReady = true;
            }
          "
          :src="originalImage"
          :viewMode="allowCropOutside ? 0 : 2"
          class="w-full h-full"
          crossorigin="anonymous"
          dragMode="move"
        />
      </div>

      <div
        v-if="others?.length"
        class="flex justify-center gap-2 min-h-[12rem] w-full"
      >
        <div class="overflow-hidden absolute left-0 top-0 w-[0] h-[0]">
          <vue-cropper
            v-for="(item, index) in others"
            :key="index"
            ref="cropperOthers"
            :aspect-ratio="cropAspectRatio"
            :checkCrossOrigin="false"
            :ready="
              () => {
                cropperReady = true;
              }
            "
            :src="item"
            :viewMode="allowCropOutside ? 0 : 2"
            crossorigin="anonymous"
          />
        </div>
      </div>
    </div>
  </overlay>
</template>
